// ** React Imports
import { useContext, useEffect, useMemo, useRef, useState } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import useResizeObserver from "@react-hook/resize-observer";

// ** Custom Components
import Block from "components/layout/Block";
import Dropdown from "components/layout/Dropdown";

// ** Selector Components
import SearchInput from "./components/SearchInput";
import MenuItems from "./components/MenuItems";
import Activator from "./components/Activator";
import { DropdownTriggerContext } from "components/layout/Dropdown/DropdownTriggerContext";
import { StyledSelectorLabel } from "./components/SelectorLabel.styled.";

// ** Custom Hooks
import useOpenClose from "hooks/useOpenClose";
import useSelected from "hooks/useSelected";

// ** Utils & Helpers
import slug from "utils/slug";
import { publish, subscribe, unsubscribe } from "helpers/events";
import {
	ACTUAL_OPEN_SELECTOR,
	IS_MULTIPLE_SELECTOR_IN_ACCEPT_MODE,
} from "helpers/eventsList";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const diff = (a, b) => b.filter((x) => !a.includes(x));

const MAX_VISIBLE_SELECTED_ITEMS = 3;

// #####################################################

const SelectorSelectMulti = ({
	selectorKey,
	label,
	items,
	activatorStyle,
	activatorStyleOptions,
	activatorWrapperStyle,
	customLayerStyle,
	emptyText: emptyTextProps,
	selected,
	onAccept,
	onSelect,
	menuFontSize,
	withMobileVersion,
	withSearchInput,
	withAdditionalInfo,
	allwaysSelected,
	selectedLimit,
	selectedLimitText,
	useLayerOptions,
	useMore,
	subscribeActualOpenSelectorEvent,
	isBan,
	customMobileLayerStyle,
	isSingleDropdown,
	isMultiDropdown,
	isKeyboardOpen,
	visualViewportHeight,
	autoFocus,
	isLanding = false,
	maxLayerHeight = 300,
}) => {
	const { t } = useTranslation(["common"]);
	const { isPhone } = useContext(deviceTypeContext);
	const language = useSelector((state) => state.globalCommon.language);
	const { isOpen, close, open, toggle } = useOpenClose();

	const extendedClose = ({ insideUseEffect = false } = {}) => {
		if (insideUseEffect) {
			close();
		} else {
			close(() => {
				if (
					document.body.style.overflow === "hidden" &&
					(isSingleDropdown || !customMobileLayerStyle)
				) {
					document.body.style.overflow = "unset";
				}
			});
		}
	};

	const acceptOnClose = () => {
		if (
			document.body.style.overflow === "hidden" &&
			(isSingleDropdown || !customMobileLayerStyle)
		) {
			document.body.style.overflow = "unset";
		}

		if (hasChanges && typeof onAccept === "function") {
			onAccept(checkedItemsArray);
		}
	};

	// ** Dropdown opcje konfiguracyjne
	const useLayerOptionsFinal = {
		onOutsideClick: () => {
			close(acceptOnClose);
		},
		onParentClose: () => {
			extendedClose();
		},
		overflowContainer: false,
		...useLayerOptions,
	};

	const customLayerStyleFinal = {
		maxHeight: `${maxLayerHeight}px`,
		zIndex: isPhone ? 1000 : 499,
		...customLayerStyle,
	};

	// ** Hook useState - wartość ustawiana na true w przypadu gdy inny selektor znajduje
	// ** się w stanie 'onAccept', aby nie pozwolić na otwarcie kolejnego selektora
	const [isOtherSelectorInAcceptMode, setOtherSelectorInAcceptMode] =
		useState(false);

	// ** Hook useState - wartość wyszukiwana i funkcja ją ustawiająca
	const [searchValue, setSearchValue] = useState("");

	// ** Tekst wyświetlany w przypadku nie wybranie elementu
	const emptyText = emptyTextProps || t("common:selectAll2");

	// ** Hook useMemo - Ostateczna lista elementów wyświetlanych w dropdown'ie
	// ** (zmodyfikowany props 'key')
	const finalItems = useMemo(
		() =>
			items.map((item, index) => ({
				...item,
				key: label
					? `${slug(label)}-item-${item.key || index}`
					: `item-${item.key || index}`,
			})),
		[items, language]
	);

	// ** Hook useMemo - Lista Wybranych Elementów (na podstawie 'selectedItems)
	const selectedItems = useMemo(
		() => finalItems.filter((item) => selected.includes(item.value)),
		[finalItems, selected, language]
	);

	// ** Hook useSelect
	const {
		selected: checkedItemsArray,
		setSelected: setCheckedItemsArray,
		check: checkItem,
		hasSelected,
	} = useSelected(
		items.map((item) => item.value),
		selected,
		allwaysSelected
	);

	// ** Efekt ustawiajacy aktualnie zaznaczone opcje np. po wywołaniu funkcji resetujacej
	useEffect(() => {
		setCheckedItemsArray(selected);
		return () => {
			setCheckedItemsArray([]);
		};
	}, [selected, language]);

	// ** Hook useMemo - Lista Aktualnie Zaznaczonych Elementów (na podstawie 'checkedItemsArray)
	const currentSelectedItems = useMemo(
		() =>
			finalItems.filter((item) => checkedItemsArray.includes(item.value)),
		[finalItems, selectedItems, checkedItemsArray]
	);

	// ** Hook useMemo - Funkcja Sprawdza Czy Aktualnie Zaznaczone Elementy (currentSelectedItems)
	// ** Różnia Się Od Wcześniej Zaznaczonych (selected)
	const hasChanges = useMemo(
		() =>
			Boolean(
				diff(
					selected,
					currentSelectedItems.map((item) => item.value)
				).length
			) ||
			Boolean(
				diff(
					currentSelectedItems.map((item) => item.value),
					selected
				).length
			),
		[selected, currentSelectedItems]
	);

	// ** Informacja o tym czy wybrano jakiś element z listy
	const isSelected = Boolean(checkedItemsArray.length);

	// **
	const andMoreNormal =
		currentSelectedItems.length > MAX_VISIBLE_SELECTED_ITEMS
			? currentSelectedItems.length - MAX_VISIBLE_SELECTED_ITEMS
			: false;

	// ** Tekst wyświetlany w aktywatorze - wybrane elementy z listy
	const displayText = isSelected
		? useMore && andMoreNormal
			? `${t("common:selected")}\u00A0(${checkedItemsArray.length})`
			: currentSelectedItems
					.slice(0, useMore ? MAX_VISIBLE_SELECTED_ITEMS : 11)
					.map((i) => i.content)
		: emptyText;

	// ** Funkcja obsługująca kliknięcie w dany element z listy
	const handleSelectItem = (item) => {
		if (!selected.includes(item.value)) {
			checkItem(item.value);
			if (typeof onSelect === "function") {
				onSelect([...checkedItemsArray, item.value]);
			}

			if (item.onClick && typeof item.onClick === "function") {
				item.onClick();
			}
		} else {
			checkItem(item.value);
			if (typeof onSelect === "function") {
				onSelect(
					checkedItemsArray.filter(
						(tempItem) => tempItem !== item.value
					)
				);
			}

			if (item.onClick && typeof item.onClick === "function") {
				item.onClick();
			}
		}
	};

	// ** Referencja do wrappera na podstawie którego jest
	// ** obliczna szerokość otwieranego dropdown'u
	const wrapperRef = useRef(null);

	// ** Efekt publikujący CustomEvent 'ACTUAL_OPEN_SELECTOR'
	// ** z 'id' (label) aktualnie otwartego selektora
	useEffect(() => {
		if (isOpen) {
			publish(ACTUAL_OPEN_SELECTOR, { selectorKey });
		}
	}, [isOpen, isPhone]);

	// ** Efekt publikujący event "IS_MULTIPLE_SELECTOR_IN_ACCEPT_MODE", blokujący
	// ** możliwość otwarcia innego selektora do czasu zakończenia akceptacji.
	useEffect(() => {
		if (hasChanges && typeof onAccept === "function") {
			publish(IS_MULTIPLE_SELECTOR_IN_ACCEPT_MODE, true);
		} else {
			publish(IS_MULTIPLE_SELECTOR_IN_ACCEPT_MODE, false);
		}
	}, [hasChanges]);

	// ** Event subskrybujący event: 'ACTUAL_OPEN_SELECTOR' w przypadku
	// ** ustawienia flagi 'subscribeActualOpenSelectorEvent' na TRUE
	useEffect(() => {
		if (subscribeActualOpenSelectorEvent) {
			subscribe(ACTUAL_OPEN_SELECTOR, (event) => {
				if (event.detail.selectorKey !== selectorKey) {
					extendedClose({ insideUseEffect: true });
				}
			});
		}

		// ** Subskrypcja event'u 'IS_MULTIPLE_SELECTOR_IN_ACCEPT_MODE'
		subscribe(IS_MULTIPLE_SELECTOR_IN_ACCEPT_MODE, (event) => {
			setOtherSelectorInAcceptMode(event.detail);
		});

		return () => {
			unsubscribe(ACTUAL_OPEN_SELECTOR);
			unsubscribe(IS_MULTIPLE_SELECTOR_IN_ACCEPT_MODE);
		};
	}, []);

	// ** Szerokość otwieranego dropdown'a ustawiona poprzez 'useResizeObserver'
	const [layerWidth, setLayerWidth] = useState(
		(activatorStyleOptions?.noPad
			? wrapperRef?.current?.clientWidth
			: wrapperRef?.current?.clientWidth - 7) *
			(activatorStyleOptions?.scaleLayer || 1)
	);

	useResizeObserver(wrapperRef, (entry) => {
		setLayerWidth(
			(activatorStyleOptions?.noPad
				? entry.contentRect.width
				: entry.contentRect.width - 7) *
				(activatorStyleOptions?.scaleLayer || 1)
		);
	});

	// #####################################################

	return (
		<Block
			ref={wrapperRef}
			style={{
				flex: activatorStyleOptions?.flex1 ? "1" : "unset",
				width: isPhone ? "100%" : "unset",
			}}
		>
			<Dropdown
				isOpen={isOpen}
				close={(insideUseEffect) => {
					extendedClose(insideUseEffect);
				}}
				useLayerOptions={useLayerOptionsFinal}
				customLayerStyle={customLayerStyleFinal}
				layerWidth={layerWidth}
				withMobileVersion={withMobileVersion}
				mobileTitle={label}
				customMobileLayerStyle={customMobileLayerStyle}
				isKeyboardOpen={isKeyboardOpen}
				visualViewportHeight={visualViewportHeight}
				isSingleDropdown={isSingleDropdown}
				isMultiDropdown={isMultiDropdown}
				autoBorder={true}
				isLanding={isLanding}
				menuContent={({ layerSide }) =>
					isOpen && (
						<Block
							full
							width="100%"
							style={{
								height: "100%",
								maxHeight: isPhone
									? "unset"
									: `${maxLayerHeight}px`,
							}}
						>
							<Block
								full
								style={{
									overflow: "auto",
									height: "100%",
									maxHeight: isPhone
										? "unset"
										: `${maxLayerHeight - 10}px`,
								}}
							>
								{layerSide === "top" && !isPhone && (
									<Block
										style={{
											padding: "0.5rem 1rem 0",
										}}
									>
										<StyledSelectorLabel>
											{label}
										</StyledSelectorLabel>
									</Block>
								)}
								{/* Opcjonalne pole wyszukiwania tekstowego  */}
								{withSearchInput && (
									<SearchInput
										searchValue={searchValue}
										setSearchValue={setSearchValue}
										autoFocus={autoFocus}
									/>
								)}

								{/* Lista elementów do wyboru  */}
								<MenuItems
									withSearchInput={withSearchInput}
									searchValue={searchValue}
									finalItems={finalItems}
									selected={checkedItemsArray}
									handleSelectItem={handleSelectItem}
									menuFontSize={menuFontSize}
									allwaysSelected={allwaysSelected}
									withAdditionalInfo={withAdditionalInfo}
									multiple
								/>
							</Block>
						</Block>
					)
				}
			>
				<DropdownTriggerContext.Consumer>
					{({ triggerProps, layerSide }) => (
						<Activator
							activatorOptions={{
								...activatorStyleOptions,
								isMultiple: true,
							}}
							activatorStyle={activatorStyle}
							activatorWrapperStyle={activatorWrapperStyle}
							innerRef={triggerProps.ref}
							label={label}
							onClick={() => {
								if (isOpen) {
									toggle(() => {
										if (
											document.body.style.overflow ===
												"hidden" &&
											(isSingleDropdown ||
												!customMobileLayerStyle)
										) {
											document.body.style.overflow =
												"unset";
										}

										if (
											hasChanges &&
											typeof onAccept === "function"
										) {
											onAccept(checkedItemsArray);
										}
									});
								} else {
									if (!isOtherSelectorInAcceptMode) {
										if (
											isPhone &&
											(isSingleDropdown ||
												!customMobileLayerStyle) &&
											document.body.style.overflow !==
												"hidden"
										) {
											document.body.style.overflow =
												"hidden";
										}
										open();
									}
								}
							}}
							isOpen={isOpen}
							isPhone={isPhone}
							displayContent={displayText}
							isSelected={isSelected}
							hasSelected={hasSelected}
							selectedLimitText={selectedLimitText}
							selectedLimit={selectedLimit}
							disabled={isBan}
							layerSide={layerSide}
						/>
					)}
				</DropdownTriggerContext.Consumer>
			</Dropdown>
		</Block>
	);
};

// #####################################################

export default SelectorSelectMulti;

// #####################################################
